/**
 * BP-026 · Feature flag management.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ToggleLeft, Plus, Trash2 } from "lucide-react";
import { apiGet, apiPost, apiDelete } from "@/lib/api";

export default function PlatformFlags() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ key: "", enabled: true, rollout_pct: 100, description: "" });

  const load = async () => {
    const r = await apiGet("/api/v3/platform/feature-flags");
    setRows(r || []);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.key) return toast.error("Key required");
    try {
      await apiPost("/api/v3/platform/feature-flags", form);
      toast.success("Flag saved");
      setForm({ key: "", enabled: true, rollout_pct: 100, description: "" });
      load();
    } catch (e) {
      toast.error(e?.message || "Save failed");
    }
  };
  const remove = async (key, workspace_id) => {
    try {
      await apiDelete(
        `/api/v3/platform/feature-flags?key=${encodeURIComponent(key)}${workspace_id ? `&workspace_id=${workspace_id}` : ""}`
      );
      toast.success("Flag removed");
      load();
    } catch (e) {
      toast.error(e?.message || "Delete failed");
    }
  };
  const toggle = async (row) => {
    await apiPost("/api/v3/platform/feature-flags", {
      key: row.key,
      workspace_id: row.workspace_id,
      enabled: !row.enabled,
      rollout_pct: row.rollout_pct,
      description: row.description,
    });
    load();
  };

  return (
    <div data-testid="platform-flags" className="p-8 space-y-6">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <ToggleLeft className="w-4 h-4" />
          Feature flags
        </div>
        <h1 className="text-2xl font-bold">Rollout controls</h1>
      </header>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase text-slate-500 mb-2">Upsert flag</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input
            data-testid="flag-key"
            value={form.key}
            onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
            placeholder="flag.key"
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
          <input
            data-testid="flag-desc"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Description"
            className="md:col-span-2 bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
          <input
            data-testid="flag-rollout"
            type="number"
            min={0}
            max={100}
            value={form.rollout_pct}
            onChange={(e) => setForm((f) => ({ ...f, rollout_pct: Number(e.target.value) }))}
            placeholder="rollout %"
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
          <button
            data-testid="flag-save"
            onClick={save}
            className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-slate-900 text-sm font-semibold flex items-center justify-center gap-1"
          >
            <Plus className="w-4 h-4" /> Save
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">Key</th>
              <th className="px-4 py-2">Scope</th>
              <th className="px-4 py-2">On</th>
              <th className="px-4 py-2">Rollout</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.map((r, i) => (
              <tr
                key={`${r.key}-${r.workspace_id || "g"}`}
                data-testid={`flag-row-${r.key}`}
              >
                <td className="px-4 py-2 font-mono text-slate-900">{r.key}</td>
                <td className="px-4 py-2 text-slate-600">
                  {r.workspace_id ? `ws:${r.workspace_id.slice(0, 8)}` : "global"}
                </td>
                <td className="px-4 py-2">
                  <button
                    data-testid={`flag-toggle-${r.key}`}
                    onClick={() => toggle(r)}
                    className={`rounded-full px-2 py-0.5 text-[11px] ${
                      r.enabled
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-slate-500/20 text-slate-600"
                    }`}
                  >
                    {r.enabled ? "ON" : "OFF"}
                  </button>
                </td>
                <td className="px-4 py-2 text-slate-600">
                  {r.rollout_pct != null ? `${r.rollout_pct}%` : "—"}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    data-testid={`flag-del-${r.key}`}
                    onClick={() => remove(r.key, r.workspace_id)}
                    className="text-red-600 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No flags yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
